import React from "react";
import styled, { keyframes } from "styled-components";

const shimmer = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`;

export const SkeletonCard = styled.div`
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.2s ease-in-out infinite;
  border-radius: 16px;
  height: 100%;
  min-height: 100px;
`;

export const SkeletonLine = styled.div<{ $width?: string; $height?: string }>`
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.2s ease-in-out infinite;
  border-radius: 8px;
  height: ${(p) => p.$height ?? "14px"};
  width: ${(p) => p.$width ?? "100%"};
`;

export const SkeletonAvatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.2s ease-in-out infinite;
  flex-shrink: 0;
`;

const SkeletonStatsRowStyled = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
`;

export function SkeletonStatsRow({ count = 3 }: { count?: number }) {
  return (
    <SkeletonStatsRowStyled>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} style={{ minHeight: "120px" }} />
      ))}
    </SkeletonStatsRowStyled>
  );
}

const SkeletonUsageSectionStyled = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.04);
`;

export function SkeletonUsageSection() {
  return (
    <SkeletonUsageSectionStyled>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
        <SkeletonLine $width="140px" $height="20px" />
        <SkeletonLine $width="80px" $height="16px" />
      </div>
      <SkeletonLine $height="12px" style={{ marginBottom: 16, borderRadius: 12 }} />
      <SkeletonLine $width="120px" $height="40px" style={{ marginTop: 8 }} />
    </SkeletonUsageSectionStyled>
  );
}

const SkeletonListRowStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.04);
`;

export function SkeletonListRows({ count = 3 }: { count?: number }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonListRowStyled key={i}>
          <SkeletonAvatar />
          <div style={{ flex: 1 }}>
            <SkeletonLine $width="60%" $height="16px" style={{ marginBottom: 8 }} />
            <SkeletonLine $width="40%" $height="12px" />
          </div>
        </SkeletonListRowStyled>
      ))}
    </div>
  );
}

export function SkeletonDashboard() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <SkeletonLine $width="220px" $height="28px" style={{ marginBottom: 8 }} />
        <SkeletonLine $width="320px" $height="14px" />
      </div>
      <SkeletonStatsRow count={3} />
      <SkeletonUsageSection />
      <SkeletonCard style={{ minHeight: "220px" }} />
    </div>
  );
}

export function SkeletonBilling() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <SkeletonLine $width="180px" $height="28px" style={{ marginBottom: 8 }} />
        <SkeletonLine $width="200px" $height="14px" />
      </div>
      <SkeletonUsageSection />
      <SkeletonCard style={{ minHeight: "200px" }} />
    </div>
  );
}

export function SkeletonCampaignOrTemplateList() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <SkeletonLine $width="200px" $height="28px" style={{ marginBottom: 8 }} />
        <SkeletonLine $width="280px" $height="14px" />
      </div>
      <SkeletonListRows count={4} />
    </div>
  );
}
